import React from 'react';
import { formatDuration } from '../../../utils/formatDuration';
import { countRu, pluralRu, ZONE_FORMS } from '../../../utils/pluralRu';
import { describeChange, formatRate, formatPercent } from '../summaryFormatters';
import { VIOLATION_VOCAB } from '../summaryVocab';

const Card = ({ title, value, sub, subTone, tooltip }) => (
  <div className="sum-kpi" title={tooltip}>
    <div className="sum-kpi-title">{title}</div>
    <div className="sum-kpi-value">{value}</div>
    {sub && <div className={`sum-kpi-sub${subTone ? ` sum-kpi-sub--${subTone}` : ''}`}>{sub}</div>}
  </div>
);

/**
 * ТЗ №1 §7.3. Все значения — из kpis как есть. active_rules и coverage —
 * current-state (§6.4): violations_count > 0 при active_rules = 0 — не баг.
 *
 * Сравнение с прошлым периодом — строка под числом, а не отдельная карточка:
 * одни данные показываются в одном месте. Без истории строки нет совсем.
 *
 * vocab — подписи вкладки (нарушения или бизнес-события). Прошлое значение
 * null остаётся «нет сравнения», а не нулём.
 */
const KpiStrip = ({ kpis, controlContinuity, comparisonState, vocab = VIOLATION_VOCAB }) => {
  const change = describeChange(
    kpis[vocab.changeDirectionKey],
    kpis[vocab.changePercentKey],
    comparisonState,
    vocab.newText
  );
  const previousCount = kpis[vocab.previousCountKey];
  const previousRate = comparisonState === 'available' ? kpis[vocab.previousRateKey] : null;
  const maxDuration = kpis[vocab.maxDurationKey];
  const controlHours = controlContinuity?.available_control_hours;
  return (
    <div className="sum-kpis">
      <Card
        title={vocab.countTitle}
        value={kpis[vocab.countKey] ?? '—'}
        sub={change?.label}
        // Рост бизнес-событий не оценивается: без красного/зелёного.
        subTone={vocab.growthIs === 'none' ? undefined : change?.tone}
        tooltip={comparisonState === 'available' && previousCount != null
          ? `В прошлом периоде ${previousCount}`
          : undefined}
      />
      <Card
        title="Суммарная длительность"
        value={kpis[vocab.durationKey] == null ? '—' : formatDuration(kpis[vocab.durationKey])}
        sub={maxDuration ? `Самое долгое ${formatDuration(maxDuration)}` : undefined}
      />
      <Card
        title="На 100 часов контроля"
        value={formatRate(kpis[vocab.rateKey])}
        sub={previousRate != null ? `В прошлом периоде ${formatRate(previousRate)}` : undefined}
        tooltip={controlHours != null ? `${controlHours} ч контроля` : undefined}
      />
      <Card
        title="Активные правила"
        value={kpis.active_rules ?? '—'}
        sub="Сейчас"
        tooltip={kpis.active_rules_definition}
      />
      <Card
        title="Покрытие"
        value={formatPercent(kpis.coverage_percent)}
        sub={kpis.total_cameras != null
          ? `${kpis.monitored_cameras ?? 0} из ${kpis.total_cameras} ${pluralRu(kpis.total_cameras, ['камеры', 'камер', 'камер'])}${kpis.monitored_zones != null ? ` · ${countRu(kpis.monitored_zones, ZONE_FORMS)}` : ''}`
          : undefined}
        tooltip={kpis.coverage_definition}
      />
    </div>
  );
};

export default KpiStrip;
